import { shiftDateKey, type BusinessSession } from "@teamna/shared";
import type { UseQueryResult } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useBusinessSchedule } from "@teamna/web-client/hooks/use-setup.ts";
import { dayKey, instantFromZonedTime } from "@teamna/web-client/lib/format.ts";
import { todayProgress, type TodayProgress } from "./progress.ts";

/** The time now, moved on every minute: a session goes from "later" to "now" while the page is open. */
function useMinuteClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

/**
 * The sessions of one day of the business, in its own clock, with their registers and marks
 * (`GET /business/businesses/:id/schedule`). The day is today, or `dayOffset` days from today (the
 * Calendar's Day view). A moved session is left out: the live one is in the list. `now` moves on
 * every minute, so a session goes from "later" to "now" while the page is open.
 */
export function useTodaySessions(
  businessId: string | null,
  timezone: string,
  dayOffset = 0,
): {
  query: UseQueryResult<BusinessSession[]>;
  sessions: BusinessSession[];
  todayKey: string;
  /** The day on screen: `todayKey` moved by `dayOffset` days. */
  shownKey: string;
  now: number;
  /** The day on screen in the business's clock: midnight to midnight, as instants. */
  range: { from: string; to: string };
} {
  const now = useMinuteClock();
  const todayKey = dayKey(new Date(now).toISOString(), timezone);
  const shownKey = shiftDateKey(todayKey, dayOffset);
  const range = useMemo(
    () => ({
      from: instantFromZonedTime(shownKey, 0, 0, timezone),
      to: instantFromZonedTime(shiftDateKey(shownKey, 1), 0, 0, timezone),
    }),
    [shownKey, timezone],
  );
  const query = useBusinessSchedule(businessId, range.from, range.to);
  const sessions = useMemo(
    () => (query.data ?? []).filter((session) => session.status !== "moved").sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
    [query.data],
  );
  return { query, sessions, todayKey, shownKey, now, range };
}

/**
 * Where attendance is for each session of a range, by session id: the Calendar's day list and Week
 * read it, so a session says there what it says on Home and on the Day view. The availability list
 * that draws those views has no registers, so this reads the schedule of the same range.
 */
export function useSessionProgress(businessId: string | null, from: string, to: string): Map<string, { session: BusinessSession; progress: TodayProgress }> {
  const query = useBusinessSchedule(businessId, from, to);
  // The same clock as Home and the Day view: a session that starts or ends while the page is open says so.
  const now = useMinuteClock();
  return useMemo(
    () => new Map((query.data ?? []).map((session) => [session.id, { session, progress: todayProgress(session, now) }])),
    [query.data, now],
  );
}
